"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";

export default function PrivacyPolicy() {
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex flex-col gap-16 pt-32 pb-16 max-w-4xl mx-auto px-6">
        {/* Header Section */}
        <div className="flex flex-col gap-6 text-center">
          <h1 className="text-h1 text-landing-primary">Privacy Policy</h1>
          <p className="text-body text-landing-secondary">Last updated: September 30, 2025</p>
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-8">
          <IntroductionSection />
          <InformationWeCollectSection />
          <HowWeUseInformationSection />
          <HowWeShareInformationSection />
          <DataRetentionSection />
          <YourRightsSection />
          <CookiesSection />
          <ThirdPartyServicesSection />
          <DataSecuritySection />
          <ChildrenPrivacySection />
          <InternationalDataTransfersSection />
          <ChangesToPolicySection />
          <ContactSection />
        </div>
      </div>
    </div>
  );
}

function IntroductionSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Introduction</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          At Nowadays AI Corp. ("we," "us," or "our"), we are committed to protecting your privacy and ensuring the
          security of your personal information. This Privacy Policy explains how we collect, use, disclose, and
          safeguard your information when you use our event planning platform and services (collectively, the
          "Services").
        </p>
        <p>
          By accessing or using our Services, you agree to the terms of this Privacy Policy. If you do not agree with
          our policies and practices, please do not use our Services.
        </p>
        <p>
          This Privacy Policy should be read in conjunction with our{" "}
          <a href="/terms-of-service" className="text-blue-500 underline">
            Terms of Service
          </a>{" "}
          and our{" "}
          <a href="/security" className="text-blue-500 underline">
            Security Policy
          </a>
          .
        </p>
      </div>
    </section>
  );
}

function InformationWeCollectSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Information We Collect</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <div className="flex flex-col gap-2">
          <p className="font-medium">1. Information You Provide Directly</p>
          <ul className="list-disc ml-6 flex flex-col gap-2">
            <li>
              <span className="font-medium">Account Information:</span> When you create an account, we collect your
              first name, last name, email address, and password (stored securely using bcrypt hashing).
            </li>
            <li>
              <span className="font-medium">Event Information:</span> Details about your events including event type,
              dates, locations, number of attendees, budget, preferences, and any other information you provide when
              planning events.
            </li>
            <li>
              <span className="font-medium">Dietary Restrictions and Sensitive Preferences:</span> When provided for
              attendee management or catering purposes, we may collect dietary restrictions and food preferences. This
              information may constitute sensitive personal data in certain jurisdictions (e.g., where it reveals
              religious beliefs or health conditions) and is handled with additional care.
            </li>
            <li>
              <span className="font-medium">Communication Data:</span> Information from your communications with us,
              including support requests, feedback, and any correspondence through our platform or email.
            </li>
            <li>
              <span className="font-medium">Payment Information:</span> When you make payments, our payment processor
              Stripe collects your payment card information. We do not store your complete payment card details on our
              servers.
            </li>
          </ul>
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">2. Information We Collect Automatically</p>
          <ul className="list-disc ml-6 flex flex-col gap-2">
            <li>
              <span className="font-medium">Usage Information:</span> We collect information about how you interact with
              our Services, including pages visited, features used, time spent on pages, and navigation patterns.
            </li>
            <li>
              <span className="font-medium">Device Information:</span> We collect information about the device you use
              to access our Services, including device type, operating system, browser type, IP address, and unique
              device identifiers.
            </li>
            <li>
              <span className="font-medium">Analytics Data:</span> We use PostHog to collect analytics and error
              tracking data to improve our Services and user experience.
            </li>
            <li>
              <span className="font-medium">Log Data:</span> Our servers automatically record information including
              access times, pages viewed, IP addresses, and the page you visited before navigating to our Services.
            </li>
          </ul>
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">3. Information from Third-Party Services</p>
          <ul className="list-disc ml-6 flex flex-col gap-2">
            <li>
              <span className="font-medium">Email Integration:</span> If you connect your Gmail account, we access your
              emails to extract venue quotes, manage RFP communications, and facilitate event planning. We only access
              emails necessary for providing our Services.
            </li>
            <li>
              <span className="font-medium">Google Maps:</span> We use Google Maps API to help you discover and evaluate
              venue locations.
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function HowWeUseInformationSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">How We Use Your Information</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>We use the information we collect for the following purposes:</p>
        <ul className="list-disc ml-6 flex flex-col gap-2">
          <li>
            <span className="font-medium">Provide Services:</span> To create and manage your account, process event
            planning requests, source venues, send RFPs, manage communications with venues, and deliver the core
            functionality of our platform.
          </li>
          <li>
            <span className="font-medium">Process Payments:</span> To process transactions, send invoices, and manage
            billing through our payment processor Stripe.
          </li>
          <li>
            <span className="font-medium">AI-Powered Features:</span> To use Anthropic Claude for email parsing, venue
            analysis, RFP generation, and contract and proposal analysis. Your data is not used to train AI models at
            the individual or event level. Nowadays AI Corp. may use aggregated, anonymized booking patterns for product
            improvement.
          </li>
          <li>
            <span className="font-medium">Communications:</span> To send you service-related emails, event updates,
            venue proposals, notifications, and respond to your inquiries.
          </li>
          <li>
            <span className="font-medium">Improve Services:</span> To analyze usage patterns, understand user
            preferences, fix bugs, improve features, and enhance overall user experience.
          </li>
          <li>
            <span className="font-medium">Customer Support:</span> To provide technical support, answer questions, and
            resolve issues through our support channels including Slack and email.
          </li>
          <li>
            <span className="font-medium">Security:</span> To detect, prevent, and respond to fraud, unauthorized
            access, security incidents, and other potentially harmful activities.
          </li>
          <li>
            <span className="font-medium">Legal Compliance:</span> To comply with legal obligations, respond to legal
            requests, enforce our Terms of Service, and protect our rights and interests.
          </li>
          <li>
            <span className="font-medium">Analytics:</span> To understand how users interact with our Services and make
            data-driven improvements using PostHog analytics.
          </li>
        </ul>
      </div>
    </section>
  );
}

function HowWeShareInformationSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">How We Share Your Information</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          We do not sell your personal information. We share your information only in the following limited
          circumstances:
        </p>

        <div className="flex flex-col gap-2">
          <p className="font-medium">1. Service Providers</p>
          <p>We share your information with trusted third-party service providers who help us deliver our Services:</p>
          <ul className="list-disc ml-6 flex flex-col gap-2">
            <li>
              <span className="font-medium">Supabase:</span> Database hosting and management (PostgreSQL) for storing
              your event data and account information.
            </li>
            <li>
              <span className="font-medium">Anthropic (Claude):</span> AI-powered features for email parsing, venue
              analysis, RFP generation, and contract and proposal analysis. Your data is not used to train AI models at
              the individual or event level. Nowadays AI Corp. may use aggregated, anonymized booking patterns for
              product improvement.
            </li>
            <li>
              <span className="font-medium">Stripe:</span> Secure payment processing for handling transactions and
              subscriptions.
            </li>
            <li>
              <span className="font-medium">Google:</span> Gmail API for email integration and Google Maps API for venue
              discovery and location services.
            </li>
            <li>
              <span className="font-medium">SendGrid:</span> Email delivery service for transactional emails and
              notifications.
            </li>
            <li>
              <span className="font-medium">PostHog:</span> Product analytics and error tracking to improve our
              Services.
            </li>
            <li>
              <span className="font-medium">Vercel:</span> Application hosting and content delivery network (CDN) for
              serving our platform.
            </li>
          </ul>
          <p className="mt-2">
            All service providers are bound by data processing agreements and are required to protect your information
            and use it only for the purposes we specify.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">2. Venues and Event Service Providers</p>
          <p>
            When you use our Services to plan events, we share relevant event information with venues and service
            providers to obtain quotes and facilitate bookings on your behalf. This includes event details such as
            dates, attendee count, your contact information, and — where applicable for hotel or rooming arrangements —
            attendee names, room assignments, dietary restrictions, and arrival information necessary to fulfill the
            booking.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">3. Legal Requirements</p>
          <p>We may disclose your information if required to do so by law or in response to:</p>
          <ul className="list-disc ml-6 flex flex-col gap-2">
            <li>Valid legal processes (court orders, subpoenas, search warrants)</li>
            <li>Legal requests from government authorities</li>
            <li>
              Circumstances necessary to protect our rights, property, or safety, or that of our users or the public
            </li>
            <li>Enforcement of our Terms of Service or investigation of potential violations</li>
          </ul>
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">4. Business Transfers</p>
          <p>
            If we are involved in a merger, acquisition, sale of assets, or bankruptcy, your information may be
            transferred as part of that transaction. We will notify you of any such change in ownership or control of
            your personal information.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">5. With Your Consent</p>
          <p>
            We may share your information with other parties when you have given us explicit consent to do so, such as
            when you authorize us to share information with event collaborators you invite to your account.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">6. Text Messaging Opt-In Data</p>
          <p>
            All the above categories exclude text messaging originator opt-in data and consent; this information will
            not be shared with any third parties.
          </p>
        </div>
      </div>
    </section>
  );
}

function DataRetentionSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Data Retention</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          We retain your personal information for as long as necessary to provide our Services and fulfill the purposes
          outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
        </p>
        <ul className="list-disc ml-6 flex flex-col gap-2">
          <li>
            <span className="font-medium">Account Data:</span> We retain your account information for as long as your
            account is active or as needed to provide you Services.
          </li>
          <li>
            <span className="font-medium">Event Data:</span> Event information is retained to maintain your event
            history and for compliance purposes.
          </li>
          <li>
            <span className="font-medium">After Account Deletion:</span> When you delete your account, we will make your
            data available for electronic retrieval for 30 days. After this period, we may delete your data, though we
            may retain certain information for legal compliance, dispute resolution, and enforcement of our agreements.
          </li>
          <li>
            <span className="font-medium">Log Data:</span> Access logs and security logs are retained for security
            monitoring and incident response purposes.
          </li>
        </ul>
      </div>
    </section>
  );
}

function YourRightsSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Your Privacy Rights</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          Depending on your location, you may have certain rights regarding your personal information. These rights may
          include:
        </p>

        <div className="flex flex-col gap-2">
          <p className="font-medium">Your Rights Include:</p>
          <ul className="list-disc ml-6 flex flex-col gap-2">
            <li>
              <span className="font-medium">Access:</span> You can request access to the personal information we hold
              about you.
            </li>
            <li>
              <span className="font-medium">Correction:</span> You can request that we correct inaccurate or incomplete
              personal information.
            </li>
            <li>
              <span className="font-medium">Deletion:</span> You can request that we delete your personal information,
              subject to certain legal exceptions.
            </li>
            <li>
              <span className="font-medium">Data Portability:</span> You can request a copy of your personal information
              in a structured, machine-readable format.
            </li>
            <li>
              <span className="font-medium">Objection:</span> You can object to certain processing of your personal
              information.
            </li>
            <li>
              <span className="font-medium">Restriction:</span> You can request that we restrict the processing of your
              personal information in certain circumstances.
            </li>
            <li>
              <span className="font-medium">Withdraw Consent:</span> Where we rely on consent to process your personal
              information, you can withdraw that consent at any time.
            </li>
          </ul>
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">GDPR Rights (European Users)</p>
          <p>
            If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you have additional
            rights under the General Data Protection Regulation (GDPR), including the rights listed above. Our legal
            basis for processing your information includes:
          </p>
          <ul className="list-disc ml-6 flex flex-col gap-2">
            <li>Performance of a contract (providing our Services to you)</li>
            <li>Legitimate interests (improving our Services, security, fraud prevention)</li>
            <li>Consent (where you have provided it)</li>
            <li>Legal obligations (compliance with applicable laws)</li>
          </ul>
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">CCPA Rights (California Users)</p>
          <p>
            If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):
          </p>
          <ul className="list-disc ml-6 flex flex-col gap-2">
            <li>Right to know what personal information we collect, use, disclose, and sell</li>
            <li>Right to request deletion of your personal information</li>
            <li>Right to opt-out of the sale of personal information (we do not sell your information)</li>
            <li>Right to non-discrimination for exercising your CCPA rights</li>
          </ul>
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">How to Exercise Your Rights</p>
          <p>
            To exercise any of these rights, please contact us at{" "}
            <a href="mailto:support@nowadays.ai" className="text-blue-500 underline">
              support@nowadays.ai
            </a>
            . We will respond to your request within 30 days (or as required by applicable law). You have the right to
            complain to a data protection authority about our collection and use of your personal information.
          </p>
        </div>
      </div>
    </section>
  );
}

function CookiesSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Cookies and Tracking Technologies</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          We use cookies and similar tracking technologies to collect information about your browsing activities and to
          personalize your experience on our Services.
        </p>

        <div className="flex flex-col gap-2">
          <p className="font-medium">Types of Cookies We Use:</p>
          <ul className="list-disc ml-6 flex flex-col gap-2">
            <li>
              <span className="font-medium">Essential Cookies:</span> Required for the Services to function properly,
              including authentication and security.
            </li>
            <li>
              <span className="font-medium">Analytics Cookies:</span> Help us understand how users interact with our
              Services through PostHog analytics.
            </li>
            <li>
              <span className="font-medium">Functional Cookies:</span> Remember your preferences and settings to enhance
              your experience.
            </li>
          </ul>
        </div>

        <p>
          You can control cookies through your browser settings. However, if you disable cookies, some features of our
          Services may not function properly.
        </p>
      </div>
    </section>
  );
}

function ThirdPartyServicesSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Third-Party Links and Services</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          Our Services may contain links to third-party websites, services, or applications that are not operated by us.
          This Privacy Policy does not apply to these third-party services. We are not responsible for the privacy
          practices of third parties. We encourage you to review the privacy policies of any third-party services before
          providing them with your personal information.
        </p>
        <p>
          When you connect third-party services (such as Gmail) to our platform, you should review their privacy
          policies to understand how they collect, use, and share your information. You can revoke access to third-party
          integrations at any time through your account settings or the third party's settings.
        </p>
      </div>
    </section>
  );
}

function DataSecuritySection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Data Security</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          We take the security of your personal information seriously and implement comprehensive security measures to
          protect it. For detailed information about our security practices, please visit our{" "}
          <a href="/security" className="text-blue-500 underline">
            Security Policy
          </a>
          .
        </p>
        <p>Key security measures include:</p>
        <ul className="list-disc ml-6 flex flex-col gap-2">
          <li>AES-256 encryption for data at rest</li>
          <li>TLS 1.3 encryption for data in transit</li>
          <li>Secure authentication using bcrypt password hashing</li>
          <li>Regular security audits and monitoring</li>
          <li>Access controls and authorization checks</li>
          <li>Automated threat detection and prevention</li>
        </ul>
        <p>
          While we implement strong security measures, no method of transmission over the internet or electronic storage
          is 100% secure. We cannot guarantee absolute security but are committed to protecting your information using
          industry best practices.
        </p>
      </div>
    </section>
  );
}

function ChildrenPrivacySection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Children's Privacy</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          Our Services are not intended for children under the age of 16, and we do not knowingly collect personal
          information from children under 16. If we become aware that we have collected personal information from a
          child under 16 without parental consent, we will take steps to delete that information as soon as possible.
        </p>
        <p>
          If you believe we have collected information from a child under 16, please contact us at{" "}
          <a href="mailto:support@nowadays.ai" className="text-blue-500 underline">
            support@nowadays.ai
          </a>
          .
        </p>
      </div>
    </section>
  );
}

function InternationalDataTransfersSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">International Data Transfers</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          Your information may be transferred to, stored, and processed in countries other than your country of
          residence, including the United States, where our servers and service providers are located. These countries
          may have data protection laws that differ from those in your country.
        </p>
        <p>
          When we transfer personal information internationally, we implement appropriate safeguards to protect your
          information in accordance with this Privacy Policy and applicable data protection laws. For transfers from the
          EEA, UK, or Switzerland, we rely on:
        </p>
        <ul className="list-disc ml-6 flex flex-col gap-2">
          <li>Standard Contractual Clauses approved by the European Commission</li>
          <li>Adequacy decisions by the European Commission</li>
          <li>Other legally approved transfer mechanisms</li>
        </ul>
      </div>
    </section>
  );
}

function ChangesToPolicySection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Changes to This Privacy Policy</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal
          requirements, or other factors. When we make changes, we will update the "Last updated" date at the top of
          this Privacy Policy.
        </p>
        <p>
          If we make material changes to this Privacy Policy, we will notify you by email (to the email address
          associated with your account) or by posting a notice on our platform before the changes take effect. We
          encourage you to review this Privacy Policy periodically to stay informed about how we collect, use, and
          protect your information.
        </p>
        <p>
          Your continued use of our Services after any changes to this Privacy Policy indicates your acceptance of the
          updated policy.
        </p>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Contact Us</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices,
          please contact us:
        </p>
        <ul className="list-disc ml-6 flex flex-col gap-2">
          <li>
            Email:{" "}
            <a href="mailto:support@nowadays.ai" className="text-blue-500 underline">
              support@nowadays.ai
            </a>
          </li>
          <li>
            Security concerns:{" "}
            <a href="mailto:eng@nowadays.ai" className="text-blue-500 underline">
              eng@nowadays.ai
            </a>
          </li>
        </ul>
        <p>
          We will respond to your inquiry within a reasonable timeframe. For data subject requests under GDPR or CCPA,
          we will respond within the timeframes required by applicable law (typically 30 days).
        </p>
        <p className="mt-4">
          <span className="font-medium">Data Protection Officer (for GDPR inquiries):</span> For questions specifically
          related to GDPR compliance or to exercise your GDPR rights, you may contact us at the email addresses above
          with "GDPR Request" in the subject line.
        </p>
      </div>
    </section>
  );
}
